import React, { useCallback, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';
import type { SpecialsCardProps } from 'src/theme/provider/commerce-components/specials-card-types';

export type SpecialsCardAdapterProps = {
  cardProps: SpecialsCardProps;
  fallback: React.ReactElement;
  renderRemote?: (card: React.ReactElement) => React.ReactElement;
};

/**
 * Creates an onLinkClick handler that intercepts only unmodified primary clicks
 * for Next.js client-side SPA routing, while preserving native anchor behavior
 * for modifiers (Ctrl/Cmd/Shift/Alt), non-primary buttons, downloads, and new tabs.
 */
export function useSpecialsLinkClickHandler(href: string): React.MouseEventHandler<HTMLAnchorElement> {
  const router = useRouter();

  return useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const target = event.currentTarget.getAttribute('target');
      if (target && target !== '_self') {
        return;
      }

      event.preventDefault();
      void router?.push(href);
    },
    [href, router]
  );
}

/**
 * Host adapter for the SpecialsCard remote component (ECOMM-13280).
 * Resolves SpecialsCard via the commerce-components registry and renders it
 * with direct props. Remote-only composition is delegated to the component
 * factory so its ErrorBoundary can replace the entire composition with the bare fallback.
 */
export function SpecialsCardAdapter({ cardProps, fallback, renderRemote }: SpecialsCardAdapterProps): JSX.Element {
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  // Stable function reference so useRemoteComponent does not recompute on every render
  const FallbackComponent: React.FC = useCallback(() => fallbackRef.current, []);

  const ResolvedSpecialsCard = useRemoteComponent<SpecialsCardProps>(RemoteComponentSlot.SpecialsCard, {
    fallback: FallbackComponent,
    renderRemote,
  });

  return <ResolvedSpecialsCard {...cardProps} />;
}
